import type { ImageScenario } from '../game/imageTypes';

/**
 * Level 6 -- a freshly raised ticket.
 *
 * Regions are measured by tools/find_arrows.py. The classification was
 * confirmed by the author on 2026-08-26: the listed mistake "no due date / no
 * estimate" covers BOTH arrows, so arrow 5 (Due date) and arrow 8 (Story
 * Points) are each a real finding.
 *
 * Image is 2000 x 871. All regions are percentages of those dimensions.
 */
export const bugReport: ImageScenario = {
  id: 'jira-bug-report',
  title: 'The ticket that tells you nothing',
  subtitle: 'Work item detail · AST1-45',

  brief:
    'Somebody raised this and moved on. Ten arrows point at parts of it. Plenty of fields are legitimately empty on a ticket this new — the question is which of these gaps actually stops anyone picking it up.',

  source: {
    toolId: 'jira',
    screen: 'Work item detail view',
    kind: 'screenshot',
    note: 'Captured from a ReleaseTEAM test instance, annotated with red arrows.',
  },

  image: {
    src: '/scenarios/bug-report.webp',
    width: 2000,
    height: 871,
    alt: 'A Jira work item AST1-45 titled "Checkout Button Is Not Working", described as "When I click on the button, nothing happens", with no acceptance criteria, priority Critical, and None in the due date, assignee, story points, sprint and fix versions fields.',
  },

  arrows: [
    {
      id: 'add-parent',
      label: '1',
      region: { x: 20.5, y: 2.0, w: 8.0, h: 7.5 },
      description: 'Arrow pointing up at the "Add parent" link in the breadcrumb',
      decoyNote:
        'No parent epic, which the "Add parent" affordance is offering to fix. Standalone work is completely normal — a one-off defect does not need to belong to an epic, and forcing everything under one produces epics that exist only to satisfy a rule.',
    },
    {
      id: 'issue-type',
      label: '2',
      region: { x: 30.5, y: 2.0, w: 7.0, h: 7.5 },
      description: 'Arrow pointing up at the work item type icon and key, AST1-45',
    },
    {
      id: 'vague-title',
      label: '3',
      // Spans the whole title, not just the arrowhead: the title IS the target,
      // and a player reading it will tap the words rather than the arrow. Starts
      // below the breadcrumb regions so the two cannot both claim a tap.
      region: { x: 1.5, y: 9.8, w: 36.5, h: 5.0 },
      description: 'Arrow pointing at the title, "Checkout Button Is Not Working"',
    },
    {
      id: 'priority-critical',
      label: '4',
      region: { x: 79.0, y: 32.5, w: 12.5, h: 6.0 },
      description: 'Arrow pointing at the Priority field, set to Critical',
      decoyNote:
        'Priority is filled in, and a checkout button that does nothing is a fair thing to call Critical — it is the last step before money changes hands. A priority you disagree with is a conversation; a priority nobody set is the actual problem, and that is not what is happening here.',
    },
    {
      id: 'no-due-date',
      label: '5',
      region: { x: 79.0, y: 40.5, w: 12.0, h: 6.5 },
      description: 'Arrow pointing at the Due date field reading None',
    },
    {
      id: 'unassigned',
      label: '6',
      region: { x: 78.0, y: 49.5, w: 14.0, h: 7.0 },
      description: 'Arrow pointing at the Assignee field reading Unassigned',
    },
    {
      id: 'no-acceptance-criteria',
      label: '7',
      region: { x: 1.5, y: 52.5, w: 8.0, h: 9.0 },
      description: 'Arrow pointing at the Acceptance Criteria section reading None',
    },
    {
      id: 'no-estimate',
      label: '8',
      region: { x: 79.0, y: 63.5, w: 11.5, h: 7.0 },
      description: 'Arrow pointing at the Story Points field reading None',
    },
    {
      id: 'no-sprint',
      label: '9',
      region: { x: 79.0, y: 73.5, w: 12.0, h: 6.5 },
      description: 'Arrow pointing at the Sprint field reading None',
      decoyNote:
        'Nothing is wrong with a ticket that is not in a sprint yet. This one was raised minutes ago and has not been through planning — putting it straight into the current sprint without anyone agreeing to it would be the mistake, not leaving this empty.',
    },
    {
      id: 'no-fix-version',
      label: '10',
      region: { x: 79.0, y: 82.5, w: 12.0, h: 6.5 },
      description: 'Arrow pointing at the Fix versions field reading None',
      decoyNote:
        'Fix version says which release the fix ships in, and that is not knowable before anyone has looked at the problem. Filling it in now would be a guess that later gets treated as a commitment. Empty is the honest state at this point.',
    },
  ],

  defects: [
    {
      id: 'wrong-type',
      arrowId: 'issue-type',
      title: 'A defect raised as a story',
      severity: 'major',
      explanation:
        'The icon on the key is the story marker, but the content is a bug report: something that used to work does not. Type drives more than an icon — it decides which reports the item appears in, which workflow it follows and whether it counts as new value or as rework. Filed as a story, this defect will be counted as delivered scope.',
      fix: 'Change the type to Bug. If the team is filing defects as stories to make the sprint look fuller, fix the incentive rather than the ticket.',
    },
    {
      id: 'vague-summary',
      arrowId: 'vague-title',
      title: 'A title that describes a symptom, not a case',
      severity: 'major',
      explanation:
        '"Checkout Button Is Not Working" tells you nothing you can act on: which button, on which page, for which users, doing what instead. The description adds only "nothing happens". Nobody can reproduce this, and nobody can tell whether a second report is the same problem or a different one.',
      fix: 'Say what was done, what was expected and what happened instead, with the page and the conditions. "Checkout button does nothing on the payment step when the basket contains a gift card" is a ticket somebody can pick up.',
    },
    {
      id: 'missing-acceptance-criteria',
      arrowId: 'no-acceptance-criteria',
      title: 'No acceptance criteria',
      severity: 'critical',
      explanation:
        'There is no statement of what "fixed" means, so there is nothing to test against and nothing to close this on except somebody\'s opinion. On a vague title this compounds: neither the developer nor the reviewer has a definition of done, and the ticket will bounce between them.',
      fix: 'Write the criteria before the work starts, as observable outcomes. On a defect, the reproduction steps that must stop reproducing are usually enough.',
    },
    {
      id: 'unassigned-critical',
      arrowId: 'unassigned',
      title: 'Nobody owns a Critical defect',
      severity: 'critical',
      explanation:
        'Marked Critical and assigned to no one. The priority says this is urgent; the empty assignee says nobody has agreed to do anything about it. That combination is how a ticket sits untouched for a week while everyone assumes somebody senior has it in hand.',
      fix: 'Anything raised as Critical should leave triage with an owner. If nobody can take it now, it is not Critical — lower the priority rather than leaving the contradiction on the ticket.',
    },
    {
      id: 'no-estimate',
      arrowId: 'no-estimate',
      title: 'No estimate',
      severity: 'major',
      explanation:
        'With no estimate this cannot be planned against anything. It cannot be fitted into a sprint, weighed against other work, or counted in a forecast — it can only be picked up and hoped about.',
      fix: 'Size it at triage, even roughly. On a defect where the cause is unknown, timebox an investigation and estimate the fix once you know what it is.',
    },
    {
      id: 'no-due-date',
      arrowId: 'no-due-date',
      title: 'No due date on urgent work',
      severity: 'minor',
      explanation:
        'Critical priority with no date attached. Priority says how this ranks against other work; a date says when it stops being acceptable for it to be open. Without one, "Critical" is a label with no consequence attached, and nothing will escalate no matter how long it sits.',
      fix: 'Put a date on work whose urgency is real. If no date can be justified, that is useful evidence the priority is wrong.',
    },
  ],
};
